import { PageHeaderWrapper } from "@clipboard-health/ui-components";
import { Box, Skeleton, Stack } from "@mui/material";

import { PageWrapper } from "../../components/PageWrapper";

function SkeletonCard(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: 4,
        padding: 5,
        marginX: 4,
      })}
    >
      {props.children}
    </Box>
  );
}

function SkeletonCardHeader() {
  return (
    <Stack direction="row" alignItems="center" spacing={3} sx={{ marginBottom: 4 }}>
      <Skeleton variant="circular" width={24} height={24} />
      <Skeleton variant="text" width="35%" height={24} />
    </Stack>
  );
}

function SkeletonRow(props: { labelWidth: string; valueWidth: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Skeleton variant="text" width={props.labelWidth} height={20} />
      <Skeleton variant="text" width={props.valueWidth} height={20} />
    </Stack>
  );
}

export function PastShiftDetailsSkeleton() {
  return (
    <PageWrapper variant="tertiary">
      {/* Header skeleton */}
      <PageHeaderWrapper>
        <Box role="progressbar" aria-label="Loading past shift details">
          {/* Top row: back button + ellipsis button */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ paddingX: 6, paddingTop: 3, paddingBottom: 5 }}
          >
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="circular" width={40} height={40} />
          </Stack>

          {/* Content area */}
          <Stack spacing={3} sx={{ paddingX: 6, paddingBottom: 7 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Skeleton variant="rounded" width={48} height={24} sx={{ borderRadius: 3 }} />
              <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: 3 }} />
            </Stack>

            <Skeleton variant="text" width="55%" height={36} />

            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Skeleton variant="text" width="30%" height={32} />
              <Skeleton variant="text" width="20%" height={24} />
            </Stack>
          </Stack>
        </Box>
      </PageHeaderWrapper>

      {/* Body content cards */}
      <Stack spacing={4} sx={{ paddingY: 4 }}>
        {/* Schedule card */}
        <SkeletonCard>
          <SkeletonCardHeader />
          <Skeleton variant="text" width="60%" height={22} />
          <Skeleton variant="text" width="40%" height={18} sx={{ marginTop: 1 }} />
        </SkeletonCard>

        {/* Timesheet card */}
        <SkeletonCard>
          <SkeletonCardHeader />
          <Stack spacing={2}>
            <SkeletonRow labelWidth="40%" valueWidth="20%" />
            <SkeletonRow labelWidth="35%" valueWidth="20%" />
            <SkeletonRow labelWidth="20%" valueWidth="15%" />
            <SkeletonRow labelWidth="25%" valueWidth="20%" />
            <Box
              sx={(theme) => ({
                height: "1px",
                backgroundColor: theme.palette.divider,
                marginY: 1,
              })}
            />
            <SkeletonRow labelWidth="35%" valueWidth="25%" />
            <SkeletonRow labelWidth="40%" valueWidth="15%" />
          </Stack>
        </SkeletonCard>

        {/* Payment History card */}
        <SkeletonCard>
          <SkeletonCardHeader />
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="circular" width={20} height={20} />
                <Stack>
                  <Skeleton variant="text" width={100} height={18} />
                  <Skeleton variant="text" width={130} height={14} />
                </Stack>
              </Stack>
              <Skeleton variant="text" width={50} height={20} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="circular" width={20} height={20} />
                <Stack>
                  <Skeleton variant="text" width={100} height={18} />
                  <Skeleton variant="text" width={130} height={14} />
                </Stack>
              </Stack>
              <Skeleton variant="text" width={50} height={20} />
            </Stack>
          </Stack>
        </SkeletonCard>

        {/* Workplace card */}
        <SkeletonCard>
          <SkeletonCardHeader />
          <Skeleton variant="text" width="70%" height={22} />
          <Skeleton variant="text" width="50%" height={16} sx={{ marginTop: 1 }} />
          <Stack direction="row" alignItems="center" spacing={2} sx={{ marginTop: 2 }}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={40} height={16} />
          </Stack>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={140}
            sx={{ marginTop: 3, borderRadius: 3 }}
          />
        </SkeletonCard>

        {/* Professionals card */}
        <SkeletonCard>
          <SkeletonCardHeader />
          <Stack spacing={3}>
            {[1, 2, 3].map((item) => (
              <Stack key={item} direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Stack>
                    <Skeleton variant="text" width={80} height={18} />
                    <Skeleton variant="text" width={50} height={14} />
                  </Stack>
                </Stack>
                <Skeleton variant="rounded" width={70} height={28} sx={{ borderRadius: 3 }} />
              </Stack>
            ))}
          </Stack>
        </SkeletonCard>
      </Stack>
    </PageWrapper>
  );
}
